import React from "react";
import { BRAND } from "../../styles/brand";
import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Zoom from "react-reveal/Slide";
import { faFilePdf } from "@fortawesome/free-solid-svg-icons";
import Transaction from "./Transaction";
import DownloadAnchor from "../common/DownloadAnchor";
import CheckoutButton from "./Payment";
import Lottie from "react-lottie";
import SuccessLottie from "../../images/success.json";
import InvoiceProgressBar from "./InvoiceProgressbar";
import { getInvoice } from "../../services/invoiceService";
import { getInvoiceReport } from "../../services/reportService";
import {
  Box,
  Card,
  CardContent,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PageHeading from "../common/PageHeading";
import CommonLoader from '../common/CommonLoader'
const ToDate = (input) => {
	if (input) {
		return new Date(input).toLocaleDateString();
	} else {
		return "";
	}
};

// Module scope on purpose: a component declared inside another component is a
// new type on every render, so React remounts its subtree instead of updating it.
const Field = ({ label, children }) => (
	<Box sx={{ mb: 2 }}>
		<Typography
			variant="caption"
			sx={{ display: "block", fontWeight: 600, color: "text.secondary" }}
		>
			{label}
		</Typography>
		{children}
	</Box>
);

export default function Invoice() {
	const { invoiceNumber } = useParams();
	const [invoice, setInvoice] = useState();
	const [percentComplete, setPercentComplete] = useState(0);
	const [successModalVisible, setSuccessModalVisible] = useState(false);
	const [zoomAnimationVisible, setZoomAnimationVisible] = useState(true);

	//Effect to run on first render
	useEffect(() => {
		const query = new URLSearchParams(window.location.search);
		if (query.get("success")) {
			setZoomAnimationVisible(false);
			setSuccessModalVisible(true);
			setTimeout(() => {
				setSuccessModalVisible(false);
				//Trigger API call to mark invoice as payment pending
			}, 3500);
		}
		if (query.get("canceled")) {
		}
	}, []);

	//Effect to run when invoiceNumber is given
	useEffect(() => {
		async function GetOrder() {
			let data = await getInvoice(invoiceNumber);
			setInvoice(data);
		}
		GetOrder();
	}, [invoiceNumber]);

	//Effect to run when invoice is loaded
	useEffect(() => {
		if (invoice?.InvoiceNumber) {
			let percent = 0;
			setTimeout(() => {
				if (invoice?.DateSent) {
					percent = 33;
				}
				if (invoice?.PaymentPending !== null) {
					percent = 66;
				}
				if (invoice?.Deposit !== 0) {
					percent =
						100 - (50 - (invoice?.Deposit / invoice?.InvoiceAmount) * 50);
				}
				setPercentComplete(percent);
			}, 1500);
		}
	}, [invoice]);

	const renderModal = () => {
		return (
			<Dialog open maxWidth="sm" PaperProps={{ sx: { p: 2 } }}>
				<Lottie
					options={{
						loop: false,
						autoplay: true,
						animationData: SuccessLottie,
					}}
					height={400}
					width={400}
				/>
			</Dialog>
		);
	};

	const RenderBody = () => {

		const balanceDue = invoice ? invoice.InvoiceAmount - invoice?.Deposit : 0;

		return (
			<Box sx={{ p: 2 }}>
				<PageHeading
					title={
						<>
							Invoice: {invoiceNumber}
							{invoice?.PoNumber?.trim()?.length ? (
								<Typography
									component="span"
									variant="body2"
									sx={{ ml: 2, color: "text.secondary", fontWeight: 400 }}
								>
									PO: {invoice?.PoNumber}
								</Typography>
							) : null}
						</>
					}
					action={
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<DownloadAnchor
								func={() => getInvoiceReport(invoiceNumber)}
								icon={faFilePdf}
								hoverText="Invoice PDF"
								text="Invoice PDF"
							/>
							<Tooltip title="Close">
								<IconButton component={Link} to="/" size="small">
									<CloseIcon />
								</IconButton>
							</Tooltip>
						</Box>
					}
				/>

				<Paper sx={{ p: 2, mb: 2 }}>
					<InvoiceProgressBar
						percentComplete={percentComplete}
						dateSent={invoice?.DateSent}
					/>
				</Paper>

				<Grid container spacing={2} sx={{ mb: 2 }}>
					<Grid size={{ xs: 12, md: 4 }}>
						<Card sx={{ height: "100%" }}>
							<CardContent>
								<Field label="Client">
									<Typography sx={{ fontWeight: 700 }}>
										{invoice?.BillingName}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										{invoice?.BillingAddress}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										{invoice?.BillingCity?.trim()},{" "}
										{invoice?.BillingState?.trim()}{" "}
										{invoice?.BillingZip?.trim()}
									</Typography>
								</Field>
							</CardContent>
						</Card>
					</Grid>

					<Grid size={{ xs: 12, md: 4 }}>
						<Card sx={{ height: "100%" }}>
							<CardContent>
								<Field label="Total Amount">
									<Typography>${invoice?.InvoiceAmount}</Typography>
								</Field>
								<Field label="Amount Paid">
									<Typography>${invoice?.Deposit}</Typography>
								</Field>
							</CardContent>
						</Card>
					</Grid>

					<Grid size={{ xs: 12, md: 4 }}>
						<Card sx={{ height: "100%" }}>
							<CardContent sx={{ textAlign: "center" }}>
								<Field label="Balance Due">
									<Typography variant="h4" sx={{ fontWeight: 700 }}>
										${balanceDue}
									</Typography>
								</Field>

								{invoice &&
								balanceDue === 0 &&
								invoice.PaymentPending == null ? null : (
									<Box>
										<Typography
											variant="caption"
											color="text.secondary"
											sx={{ display: "block", mb: 1 }}
										>
											If you have already paid and the status hasn't changed
											try logging out and checking again
										</Typography>
										<CheckoutButton invoices={invoiceNumber} />
									</Box>
								)}
							</CardContent>
						</Card>
					</Grid>
				</Grid>

				<Card sx={{ mb: 2 }}>
					<CardContent>
						<Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
							Order Info
						</Typography>
						<Divider sx={{ mb: 2 }} />

						<Grid container spacing={2}>
							<Grid size={{ xs: 12, md: 6 }}>
								<Field label="Order Number">
									<Link
										to={`/order/${invoice?.OrderNumber}`}
										style={{
											color: BRAND.primary,
											fontWeight: 600,
											textDecoration: "underline",
										}}
									>
										{invoice?.OrderNumber}
									</Link>
								</Field>
								<Field label="Received">
									<Typography>
										{ToDate(invoice?.Order?.DateReceived)}
									</Typography>
								</Field>
								<Field label="Description">
									<Typography>{invoice?.Order?.ProjectCategory}</Typography>
								</Field>
							</Grid>

							<Grid size={{ xs: 12, md: 6 }}>
								<Field label="Notes">
									<TextField
										value={invoice?.ReportNotes || ""}
										multiline
										rows={4}
										fullWidth
										size="small"
										InputProps={{ readOnly: true }}
									/>
								</Field>
							</Grid>
						</Grid>
					</CardContent>
				</Card>

				<Card>
					<CardContent>
						<Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
							Charges
						</Typography>
						<Divider sx={{ mb: 2 }} />
						{invoice?.Transactions?.map((t) => {
							return <Transaction key={t.RecordId} trans={t} />;
						})}
					</CardContent>
				</Card>
			</Box>
		);
	};

	return (
		<div>
			{successModalVisible && renderModal()}
			{!invoice ? (
				<>
				<CommonLoader loading={true} />
				</>
			) : zoomAnimationVisible ? (
				<Zoom top>{RenderBody()}</Zoom>
			) : (
				RenderBody()
			)}
		</div>
	);
}
